/**
 * Geometry for a 2-D spreadsheet grid: cells, rectangles, and the index math
 * behind range selection and Excel-style drag-fill. Pure functions — no Vue,
 * no DOM — and grid-agnostic (they never touch column names or row data
 * directly; callers inject `readCell`/`colName`). This keeps the fiddly modulo
 * tiling and rectangle-expansion logic unit-testable on its own.
 */

export interface Cell {
  ri: number;
  ci: number;
}

export interface Rect {
  r1: number;
  r2: number;
  c1: number;
  c2: number;
}

/** Stable string key for a cell, used by the non-contiguous multi-select set. */
export function cellKey(ri: number, ci: number): string {
  return `${ri}-${ci}`;
}

/** Inverse of {@link cellKey}. */
export function parseCellKey(key: string): Cell {
  const [r, c] = key.split('-');
  return { ri: Number(r), ci: Number(c) };
}

/** The inclusive rectangle spanned by two corners, normalized so r1≤r2, c1≤c2. */
export function rectBetween(a: Cell, b: Cell): Rect {
  return {
    r1: Math.min(a.ri, b.ri),
    r2: Math.max(a.ri, b.ri),
    c1: Math.min(a.ci, b.ci),
    c2: Math.max(a.ci, b.ci),
  };
}

/** Whether (ri, ci) falls inside the (inclusive) rectangle. */
export function inRect(rect: Rect | null, ri: number, ci: number): boolean {
  return !!rect && ri >= rect.r1 && ri <= rect.r2 && ci >= rect.c1 && ci <= rect.c2;
}

/** Every [ri, ci] pair inside the rectangle, row-major. */
export function cellsInRect(rect: Rect): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let r = rect.r1; r <= rect.r2; r++) {
    for (let c = rect.c1; c <= rect.c2; c++) out.push([r, c]);
  }
  return out;
}

/**
 * Extend a selection rectangle toward a hovered `target` along the dominant
 * axis, so the fill preview always stays a rectangle rather than an L-shape.
 * Returns null when the target is inside (or equal to) the selection.
 */
export function expandFillRect(sel: Rect, target: Cell): Rect | null {
  const down =
    target.ri > sel.r2 ? target.ri - sel.r2 : target.ri < sel.r1 ? sel.r1 - target.ri : 0;
  const side =
    target.ci > sel.c2 ? target.ci - sel.c2 : target.ci < sel.c1 ? sel.c1 - target.ci : 0;
  if (down === 0 && side === 0) return null;
  if (down >= side) {
    return { r1: Math.min(sel.r1, target.ri), r2: Math.max(sel.r2, target.ri), c1: sel.c1, c2: sel.c2 };
  }
  return { r1: sel.r1, r2: sel.r2, c1: Math.min(sel.c1, target.ci), c2: Math.max(sel.c2, target.ci) };
}

/**
 * Tile the source selection's values across the fill rectangle, wrapping the
 * source pattern (modulo its width/height) and leaving the source cells
 * untouched. Returns a `rowIndex → { columnName: value }` map ready to persist.
 * Grid-agnostic: `readCell` supplies a source value and `colName` maps a column
 * index to its persisted key.
 */
export function tileFillMap(
  sel: Rect,
  fill: Rect,
  readCell: (r: number, c: number) => string | null,
  colName: (c: number) => string,
): Map<number, Record<string, string | null>> {
  const h = sel.r2 - sel.r1 + 1;
  const w = sel.c2 - sel.c1 + 1;
  const perRow = new Map<number, Record<string, string | null>>();
  for (let r = fill.r1; r <= fill.r2; r++) {
    for (let c = fill.c1; c <= fill.c2; c++) {
      if (r >= sel.r1 && r <= sel.r2 && c >= sel.c1 && c <= sel.c2) continue; // leave the source
      const srcR = sel.r1 + (((r - sel.r1) % h) + h) % h;
      const srcC = sel.c1 + (((c - sel.c1) % w) + w) % w;
      const map = perRow.get(r) ?? {};
      map[colName(c)] = readCell(srcR, srcC);
      perRow.set(r, map);
    }
  }
  return perRow;
}
